import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  ListItem,
  OrderedList,
  Select,
  Stack,
  Text,
  Textarea
} from '@chakra-ui/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'
import { useAuthContext } from 'context/auth'
import Grid from 'components/TailwindUIToolkit/Grid/BaseGrid'
import type { ColDef } from 'ag-grid-community'
import {
  ListingCommand,
  ListingManagement,
  Promotion,
  showingMethods,
  vacancyListingApi
} from 'lib/vacancy-listing'
import { readinessErrorMessage } from 'lib/vacancy-readiness'

type Draft = Omit<ListingCommand, 'client_key'> & {
  status: 'keep' | 'activate' | 'remove'
  acknowledged: boolean
}
const statusCode = (e: any) => e?.statusCode ?? e?.status
const supportedPromotion = (promotion: Promotion) =>
  promotion.category === 'rent' && ['one_time', 'monthly'].includes(promotion.frequency)
const cancelablePromotion = (promotion: Promotion) =>
  ['rent', 'option_premium'].includes(promotion.category) &&
  ['one_time', 'monthly'].includes(promotion.frequency)
const categoryLabel = (value: string) =>
  ({ rent: 'Rent', option_premium: 'Option premium', new_construction: 'Amenity fee (legacy)' }[
    value
  ] ?? value.replace(/_/g, ' '))
const columns: ColDef<Promotion>[] = [
  { field: 'start_date', headerName: 'Starts', width: 115 },
  { field: 'end_date', headerName: 'Ends', width: 115 },
  {
    field: 'amount',
    headerName: 'Amount',
    width: 110,
    valueFormatter: ({ value }) =>
      Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  },
  {
    field: 'category',
    headerName: 'Applies to',
    width: 180,
    autoHeight: true,
    cellRenderer: ({ value }: { value: string }) => (
      <Badge
        my={2}
        maxW='full'
        whiteSpace='normal'
        lineHeight='18px'
        colorScheme={value === 'rent' ? 'cyan' : 'purple'}
      >
        {categoryLabel(value)}
      </Badge>
    )
  },
  {
    field: 'canceled_at',
    headerName: 'Canceled',
    width: 130,
    cellRenderer: ({ value }: { value: string | null }) => (
      <Badge colorScheme={value ? 'red' : 'gray'}>{value ? 'Yes' : 'No'}</Badge>
    )
  },
  {
    field: 'display_message',
    headerName: 'Message',
    minWidth: 200,
    flex: 1,
    tooltipField: 'display_message'
  }
]
const RecentChanges = ({
  history,
  promotions = false
}: {
  history: ListingManagement['history']
  promotions?: boolean
}) => (
  <Box
    as='details'
    borderTopWidth={1}
    pt={3}
    aria-label={promotions ? 'Promotion change history' : 'Listing change history'}
  >
    <Box as='summary' cursor='pointer' fontSize='sm' color='gray.600'>
      Recent changes{history.length ? ` (${history.length})` : ''}
    </Box>
    <Stack mt={2} spacing={0} maxH='240px' overflowY='auto'>
      {!history.length && (
        <Text fontSize='sm' color='gray.600'>
          N/A
        </Text>
      )}
      {history.map((row) => (
        <Box key={row.client_key} py={2} borderBottomWidth={1}>
          <HStack fontSize='sm' flexWrap='wrap'>
            <Text>
              {new Date(row.recorded_at).toLocaleString()} · {row.actor_name}
            </Text>
            <Badge colorScheme={promotions ? 'purple' : 'blue'} whiteSpace='normal'>
              {(
                {
                  listing_settings: 'Listing settings updated',
                  promotion_save: 'Promotion created or updated',
                  promotion_end: 'Promotion ended',
                  leasing_assignments: 'Leasing assignment updated'
                } as Record<string, string>
              )[row.action] || row.action.replace(/_/g, ' ')}
            </Badge>
          </HStack>
          <Text fontSize='sm' whiteSpace='pre-wrap' overflowWrap='anywhere'>
            {row.reason}
          </Text>
        </Box>
      ))}
    </Stack>
  </Box>
)

export default function ListingManagementPanel({
  vacancyPeriodId,
  disabled,
  onEditingChange,
  onSaved
}: {
  vacancyPeriodId: string
  disabled: boolean
  onEditingChange: (v: boolean) => void
  onSaved: () => void
}) {
  const { firebaseUser } = useAuthContext()
  const client = useQueryClient()
  const key = ['vacancy-listing-management', firebaseUser?.uid, vacancyPeriodId]
  const [page, setPage] = useState(1),
    [open, setOpen] = useState(false)
  const query = useQuery({
    queryKey: [...key, page],
    queryFn: () => vacancyListingApi.read(vacancyPeriodId, page),
    enabled: !!firebaseUser,
    retry: false,
    keepPreviousData: true
  })
  const current = query.data
  const [draft, setDraft] = useState<Draft | null>(null),
    [selected, setSelected] = useState<Promotion | null>(null)
  const [error, setError] = useState<string | null>(null),
    [conflict, setConflict] = useState(false),
    [saving, setSaving] = useState(false),
    [saved, setSaved] = useState(false)
  const sending = useRef(false),
    attempt = useRef<{ fingerprint: string; command: ListingCommand } | null>(null)
  useEffect(() => {
    onEditingChange(!!draft)
    return () => onEditingChange(false)
  }, [!!draft, onEditingChange])
  const update = (patch: Partial<Draft>) => {
    if (sending.current) return
    attempt.current = null
    setDraft((old) => (old ? { ...old, ...patch } : old))
  }
  const begin = (action: ListingCommand['action'], promotion?: Promotion) => {
    if (!current?.editable || disabled || draft || query.isFetching || query.isError) return
    if (
      promotion &&
      !(action === 'promotion_end' ? cancelablePromotion(promotion) : supportedPromotion(promotion))
    )
      return
    attempt.current = null
    setError(null)
    setConflict(false)
    setSaved(false)
    setDraft({
      action,
      expected_token: current.token,
      reason: '',
      status: 'keep',
      acknowledged: false,
      ...(action === 'listing_settings' ? { showing_type: current.listing.showing_type } : {}),
      ...(action === 'promotion_save'
        ? {
            promotion: promotion
              ? {
                  id: promotion.id,
                  token: promotion.token,
                  amount: promotion.amount,
                  start_date: promotion.start_date,
                  end_date: promotion.end_date,
                  display_message: promotion.display_message
                }
              : {
                  amount: '',
                  start_date: '',
                  end_date: '',
                  display_message: ''
                }
          }
        : {}),
      ...(action === 'promotion_end' && promotion
        ? { promotion_id: promotion.id, promotion_token: promotion.token }
        : {})
    })
  }
  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!draft || sending.current || conflict) return
    const fingerprint = JSON.stringify(draft)
    if (attempt.current?.fingerprint !== fingerprint) {
      if (!draft.reason.trim()) {
        setError('Add a note explaining this change.')
        return
      }
      if (draft.action === 'listing_settings' && draft.status === 'remove' && !draft.acknowledged) {
        setError('Review and acknowledge the effects of removing this listing.')
        return
      }
      const { status, acknowledged, ...command } = draft
      attempt.current = {
        fingerprint,
        command: {
          ...command,
          client_key: uuid(),
          reason: draft.reason.trim(),
          ...(draft.action === 'listing_settings' && status !== 'keep'
            ? {
                active: status === 'activate',
                ...(status === 'remove' ? { deactivation_acknowledged: true } : {})
              }
            : {})
        }
      }
    }
    sending.current = true
    setSaving(true)
    setError(null)
    try {
      await vacancyListingApi.save(vacancyPeriodId, attempt.current.command)
      setDraft(null)
      attempt.current = null
      setSaved(true)
      setPage(1)
      setSelected(null)
      await client.invalidateQueries({ queryKey: key })
      onSaved()
    } catch (e) {
      setError(readinessErrorMessage(e))
      setConflict(statusCode(e) === 409)
    } finally {
      sending.current = false
      setSaving(false)
    }
  }
  const refresh = async () => {
    const result = await query.refetch()
    if (!result.data || result.isError) {
      setError('Could not refresh listing data. Your entries have been kept.')
      return
    }
    const data = result.data
    if (draft?.promotion_id || draft?.promotion?.id) {
      const id = draft.promotion_id ?? draft.promotion?.id
      const latest = data.promotions.items.find((p) => p.id === id)
      if (!latest) {
        setError('This promotion is no longer on this page. Cancel this edit and select it again.')
        return
      }
      update({
        expected_token: data.token,
        ...(draft.action === 'promotion_end'
          ? { promotion_token: latest.token }
          : { promotion: { ...draft.promotion!, token: latest.token } })
      })
    } else update({ expected_token: data.token })
    setConflict(false)
    setError(
      'Latest saved values are shown above. Your entries have been kept; review them before saving again.'
    )
  }
  const editor = draft && (
    <Box as='form' onSubmit={save} borderWidth={1} borderColor='teal.400' borderRadius='md' p={3}>
      <Stack spacing={3}>
        <Heading size='sm'>
          {draft.action === 'listing_settings'
            ? 'Edit listing settings'
            : draft.action === 'promotion_end'
            ? 'End or cancel promotion'
            : draft.promotion?.id
            ? 'Edit promotion'
            : 'New promotion'}
        </Heading>
        {draft.action === 'listing_settings' && (
          <>
            <FormControl isRequired>
              <FormLabel htmlFor='listing-showing'>Showing method</FormLabel>
              <Select
                id='listing-showing'
                value={draft.showing_type}
                isDisabled={saving}
                onChange={(e) => update({ showing_type: e.target.value })}
              >
                {!Object.keys(showingMethods).includes(draft.showing_type ?? '') && (
                  <option value={draft.showing_type}>Choose a supported method</option>
                )}
                {Object.entries(showingMethods).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <Text fontSize='sm' color='gray.600'>
              Choosing a method does not provision locks or change provider accounts. Complete the
              usual provider setup separately.
            </Text>
            <FormControl>
              <FormLabel htmlFor='listing-status'>Listing status</FormLabel>
              <Select
                id='listing-status'
                value={draft.status}
                isDisabled={saving}
                onChange={(e) =>
                  update({ status: e.target.value as Draft['status'], acknowledged: false })
                }
              >
                <option value='keep'>Keep existing status</option>
                <option value='activate'>Activate listing</option>
                <option value='remove'>Remove listing</option>
              </Select>
            </FormControl>
            {draft.status === 'activate' && (
              <Alert status='info'>
                Activate this home for advertising using its saved live listing dates. Prices,
                ownership, occupancy, and matching vacancy estimates are checked before saving.
              </Alert>
            )}
            {draft.status === 'remove' && (
              <>
                <Alert status='warning' display='block'>
                  <Text>Removing this listing will:</Text>
                  <OrderedList mt={1}>
                    <ListItem>Cancel upcoming tours for this home.</ListItem>
                    <ListItem>Close active leads for this home.</ListItem>
                  </OrderedList>
                  <Text mt={2}>Open rental applications are not automatically canceled.</Text>
                </Alert>
                <Checkbox
                  isChecked={draft.acknowledged}
                  isDisabled={saving}
                  onChange={(e) => update({ acknowledged: e.target.checked })}
                >
                  I have reviewed these effects and want to remove this listing.
                </Checkbox>
              </>
            )}
          </>
        )}
        {draft.action === 'promotion_save' && draft.promotion && (
          <>
            {(['amount', 'start_date', 'end_date', 'display_message'] as const).map((field) => (
              <FormControl key={field} isRequired>
                <FormLabel htmlFor={'promo-' + field}>
                  {
                    {
                      amount: 'Discount amount ($)',
                      start_date: 'Start date',
                      end_date: 'End date',
                      display_message: 'Advertised message'
                    }[field]
                  }
                </FormLabel>
                <Input
                  id={'promo-' + field}
                  type={field.endsWith('_date') ? 'date' : 'text'}
                  inputMode={field === 'amount' ? 'decimal' : undefined}
                  maxLength={field === 'display_message' ? 2000 : undefined}
                  value={draft.promotion![field]}
                  isDisabled={saving}
                  onChange={(e) =>
                    update({
                      promotion: { ...draft.promotion!, [field]: e.target.value }
                    })
                  }
                />
              </FormControl>
            ))}
            <Text fontSize='sm'>
              This advertises a rent concession. The leasing team adds the actual credit when
              preparing the lease. Listed/base rents and existing lease or application amounts are
              kept.
            </Text>
          </>
        )}
        {draft.action === 'promotion_end' && (
          <Alert status='warning'>
            End this promotion now, including if it starts today or in the future. Its original
            dates and history are kept; it will be excluded from new listing feeds.
          </Alert>
        )}
        <FormControl isRequired>
          <FormLabel htmlFor='listing-note'>Reason for change</FormLabel>
          <Textarea
            id='listing-note'
            value={draft.reason}
            maxLength={2000}
            isDisabled={saving}
            onChange={(e) => update({ reason: e.target.value })}
          />
        </FormControl>
        {error && (
          <Alert status='error' display='block'>
            {error}
            {conflict && (
              <Button size='sm' mt={2} isLoading={query.isFetching} onClick={refresh}>
                Refresh saved values and keep my entries
              </Button>
            )}
          </Alert>
        )}
        {error && attempt.current && !conflict && (
          <Text fontSize='sm'>Retrying sends the same request. Your entries have been kept.</Text>
        )}
        <HStack>
          <Button
            variant='ghost'
            isDisabled={saving}
            onClick={() => {
              setDraft(null)
              attempt.current = null
              setError(null)
              setConflict(false)
            }}
          >
            Cancel
          </Button>
          <Button
            colorScheme={
              draft.action === 'promotion_end' || draft.status === 'remove' ? 'red' : 'teal'
            }
            type='submit'
            isLoading={saving}
            isDisabled={conflict}
          >
            {draft.action === 'promotion_end'
              ? 'End promotion'
              : draft.status === 'remove'
              ? 'Remove listing'
              : 'Save change'}
          </Button>
        </HStack>
      </Stack>
    </Box>
  )
  if (query.isError && statusCode(query.error) === 403) return null
  const canStart = !!current?.editable && !disabled && !draft && !query.isFetching && !query.isError
  return (
    <Stack spacing={5} minW={0}>
      {query.isLoading && <Text role='status'>Loading listing settings…</Text>}
      {query.isError && (
        <Alert status='error' display='block'>
          {readinessErrorMessage(query.error)}{' '}
          <Button size='sm' onClick={() => query.refetch()}>
            Retry listing settings
          </Button>
        </Alert>
      )}
      {saved && <Alert status='success'>Listing change saved.</Alert>}
      {current && (
        <>
          <Stack
            spacing={3}
            borderWidth={1}
            borderTopWidth={3}
            borderTopColor='blue.400'
            borderRadius='lg'
            p={4}
            minW={0}
          >
            <Heading size='sm' color='blue.800'>
              Listing management
            </Heading>
            {current.ineligibility_message && (
              <Text fontSize='sm'>
                {current.ineligibility_message.replace('Prices', 'Listing settings')}
              </Text>
            )}
            <HStack spacing={3} flexWrap='wrap' fontSize='sm'>
              <HStack>
                <Text color='gray.600'>Status</Text>
                <Badge
                  colorScheme={current.listing.internal_status === 'removed' ? 'gray' : 'blue'}
                >
                  {current.listing.internal_status?.replace(/_/g, ' ') ?? 'Not configured'}
                </Badge>
              </HStack>
              <HStack>
                <Text color='gray.600'>Availability</Text>
                <Badge colorScheme={current.listing.is_active ? 'green' : 'gray'}>
                  {current.listing.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </HStack>
            </HStack>
            <HStack fontSize='sm' flexWrap='wrap'>
              <Text color='gray.600'>Showing method</Text>
              <Badge colorScheme='purple'>
                {showingMethods[current.listing.showing_type as keyof typeof showingMethods] ??
                  (current.listing.showing_type === 'rently_self_showing'
                    ? 'Rently self showing'
                    : current.listing.showing_type?.replace(/_/g, ' ') || 'Not configured')}
              </Badge>
            </HStack>
            <HStack flexWrap='wrap'>
              <Button
                size='sm'
                variant='outline'
                isDisabled={!canStart}
                onClick={() => begin('listing_settings')}
              >
                Edit listing settings
              </Button>
            </HStack>
            {draft?.action === 'listing_settings' && editor}
            <RecentChanges
              history={current.history.filter((row) => !row.action.startsWith('promotion_'))}
            />
          </Stack>
          <Stack
            spacing={3}
            borderWidth={1}
            borderTopWidth={3}
            borderTopColor='purple.400'
            borderRadius='lg'
            p={4}
            minW={0}
          >
            <Heading size='sm' color='purple.800'>
              Promotions
            </Heading>
            {draft && draft.action !== 'listing_settings' && editor}
            <HStack>
              <Button variant='link' size='sm' aria-expanded={open} onClick={() => setOpen(!open)}>
                {open ? 'Hide' : 'Show'} promotions ({current.promotions.total_count})
              </Button>
              <Button
                size='sm'
                variant='outline'
                isDisabled={!canStart}
                onClick={() => {
                  setOpen(true)
                  begin('promotion_save')
                }}
              >
                Add promotion
              </Button>
            </HStack>
            {open && (
              <Stack spacing={2} minW={0}>
                <Text fontSize='sm' color='gray.600'>
                  All home promotions, newest start date first. Select a row to read its message and
                  manage it.
                </Text>
                <Grid<Promotion>
                  columns={columns}
                  rowData={current.promotions.items}
                  containerStyle={{ height: 248, width: '100%' }}
                  defaultColDefOverrides={{
                    sortable: false,
                    filter: false,
                    resizable: true,
                    autoHeight: false,
                    wrapText: false,
                    wrapHeaderText: true,
                    autoHeaderHeight: true
                  }}
                  optionOverrides={{
                    sideBar: false,
                    rowHeight: 40,
                    headerHeight: 36,
                    suppressScrollOnNewData: true,
                    enableBrowserTooltips: true
                  }}
                  getRowId={({ data }) => data.id}
                  onRowClicked={({ data }) => setSelected(data ?? null)}
                  onCellKeyDown={({ data, event }) => {
                    if ((event as KeyboardEvent)?.key === 'Enter') setSelected(data ?? null)
                  }}
                />
                {selected && (
                  <Box borderWidth={1} p={2} borderRadius='md'>
                    <Text fontSize='sm' whiteSpace='pre-wrap'>
                      {selected.display_message}
                    </Text>
                    <HStack mt={2}>
                      <Button
                        size='sm'
                        isDisabled={
                          !canStart || !!selected.canceled_at || !supportedPromotion(selected)
                        }
                        onClick={() => begin('promotion_save', selected)}
                      >
                        Edit selected promotion
                      </Button>
                      <Button
                        size='sm'
                        colorScheme='red'
                        variant='outline'
                        isDisabled={
                          !canStart || !!selected.canceled_at || !cancelablePromotion(selected)
                        }
                        onClick={() => begin('promotion_end', selected)}
                      >
                        End selected promotion
                      </Button>
                    </HStack>
                    {!supportedPromotion(selected) && (
                      <Text fontSize='sm' mt={2}>
                        {selected.category === 'option_premium'
                          ? 'Legacy option-premium offer. You can cancel it; new and edited promotions are rent-only.'
                          : `${categoryLabel(
                              selected.category
                            )} is a legacy record. This screen manages rent promotions only.`}
                      </Text>
                    )}
                  </Box>
                )}
                {current.promotions.total_count > 25 && (
                  <HStack>
                    <Button
                      size='sm'
                      isDisabled={page === 1 || query.isFetching || query.isError || !!draft}
                      onClick={() => {
                        setPage(page - 1)
                        setSelected(null)
                      }}
                    >
                      Newer promotions
                    </Button>
                    <Text fontSize='sm'>
                      Page {page} of {Math.ceil(current.promotions.total_count / 25)}
                    </Text>
                    <Button
                      size='sm'
                      isDisabled={
                        page * 25 >= current.promotions.total_count ||
                        query.isFetching ||
                        query.isError ||
                        !!draft
                      }
                      onClick={() => {
                        setPage(page + 1)
                        setSelected(null)
                      }}
                    >
                      Older promotions
                    </Button>
                  </HStack>
                )}
              </Stack>
            )}
            <RecentChanges
              promotions
              history={current.history.filter((row) => row.action.startsWith('promotion_'))}
            />
          </Stack>
        </>
      )}
    </Stack>
  )
}
